import { createContext, useContext, useMemo, type ReactNode } from "react";
import { createDocsApi, type DocsApi } from "../shared/api/endpoints";
import { isProductId, productInfo, type ProductId, type ProductInfo } from "../shared/lib/products";

/**
 * How the reader learned its product (ADR-0046 decision 4).
 *
 * - `host`: the page is on a product's docs hostname, listed in `env.js`. Routes are `/`,
 *   `/category/:slug` and `/docs/:slug`.
 * - `prefix`: any other hostname — the console's, or localhost. Routes carry the product:
 *   `/docs/:product`, `/docs/:product/category/:slug` and `/docs/:product/:slug`.
 */
export type DocsMode = "host" | "prefix";

/** The product a docs hostname belongs to, or null when the hostname is not a docs hostname. */
export function productForHost(
  hostname: string,
  config = window.RIGO_SUPPORT_WEB,
): ProductId | null {
  const product = config?.docsHosts?.[hostname.toLowerCase()];
  return isProductId(product) ? product : null;
}

export interface DocsPaths {
  home: string;
  category: (slug: string) => string;
  article: (slug: string) => string;
}

export function docsPaths(mode: DocsMode, product: ProductId): DocsPaths {
  const enc = encodeURIComponent;
  if (mode === "host") {
    return {
      home: "/",
      category: (slug) => `/category/${enc(slug)}`,
      article: (slug) => `/docs/${enc(slug)}`,
    };
  }
  const base = `/docs/${product}`;
  return {
    home: base,
    category: (slug) => `${base}/category/${enc(slug)}`,
    article: (slug) => `${base}/${enc(slug)}`,
  };
}

interface DocsContextValue {
  mode: DocsMode;
  product: ProductId;
  info: ProductInfo;
  paths: DocsPaths;
  api: DocsApi;
}

const DocsContext = createContext<DocsContextValue | null>(null);

export function DocsProvider({
  mode,
  product,
  children,
}: {
  mode: DocsMode;
  product: ProductId;
  children: ReactNode;
}) {
  const value = useMemo<DocsContextValue>(
    () => ({
      mode,
      product,
      info: productInfo(product),
      paths: docsPaths(mode, product),
      api: createDocsApi(product),
    }),
    [mode, product],
  );
  return <DocsContext.Provider value={value}>{children}</DocsContext.Provider>;
}

export function useDocs(): DocsContextValue {
  const ctx = useContext(DocsContext);
  if (!ctx) throw new Error("useDocs must be used inside <DocsProvider>");
  return ctx;
}
